/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';

import { Toast } from 'sharedUi/Toast';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';

import FormDatePicker from '../../components/forms/form-date-picker';
import FormInput from '../../components/forms/form-input';
import PhoneField from '../../components/forms/phone-field';
import FormSelect from '../../components/forms/form-select';
import FormSearchableSelect from '../../components/forms/form-searchable-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import { masterService } from '../../services/master.service';
import { useMinimalUnits } from '../../hooks/use-units';
import { ministriesService } from '../../services/ministries.service';
import { personnelService } from '../../services/personnel.service';
import { projectsService } from '../../services/projects.service';
import { useProjectScope } from '../../hooks/useProjectScope';
import { extractErrorMessage } from '../../utils/error-handler';
import { PageSkeleton } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';

// -----------------------------------------------------------------------------
// Zod Validation Schema — mirrors the flat BE personnel create/update contract
// -----------------------------------------------------------------------------
const personnelSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .max(255, 'Email must not exceed 255 characters')
    .trim()
    .toLowerCase(),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must not exceed 255 characters')
    .trim()
    .refine((val) => (val.match(/[a-zA-Z]/g) || []).length >= 3, {
      message: 'Name must contain at least 3 alphabetic characters',
    }),
  firstName: z.string().max(80, 'Must be ≤ 80 characters').trim().optional().or(z.literal('')),
  lastName: z.string().max(80, 'Must be ≤ 80 characters').trim().optional().or(z.literal('')),
  // PhoneField emits E.164, so a self-contained validity check enforces the
  // country-correct digit count without needing a separate country field —
  // the same rule the Users form applies. Optional here, unlike Users.
  phoneNo: z
    .string()
    .refine((v) => !v || isValidPhoneNumber(v), 'Enter a valid phone number for the selected country')
    .optional()
    .or(z.literal('')),
  gender: z.enum(['male', 'female']).optional().or(z.literal('')),
  title: z.string().max(32, 'Must be ≤ 32 characters').trim().optional().or(z.literal('')),
  isOnDuty: z.boolean(),
  appId: z.string().trim().optional().or(z.literal('')),
  ministryId: z.string().min(1, 'Ministry is required'),
  departmentId: z.string().min(1, 'Department is required'),
  // Required only when the selected project uses units (unitModel !== 'none').
  // That rule depends on the chosen project, so it is enforced in onSubmit, not here.
  unitId: z.string().optional().or(z.literal('')),
  designationId: z.string().optional().or(z.literal('')),
  // attributes (free-form) — expose a couple of common keys
  badgeNo: z.string().max(50, 'Must be ≤ 50 characters').optional().or(z.literal('')),
  dateOfBirth: z.string().optional().or(z.literal('')),
});

type PersonnelFormData = z.infer<typeof personnelSchema>;

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

const PersonnelForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'personnel',
    basePath: '/personnel',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<PersonnelFormData>({
    resolver: zodResolver(personnelSchema),
    defaultValues: {
      email: '',
      name: '',
      firstName: '',
      lastName: '',
      phoneNo: '',
      gender: '',
      title: '',
      isOnDuty: false,
      appId: '',
      ministryId: '',
      departmentId: '',
      unitId: '',
      designationId: '',
      badgeNo: '',
      dateOfBirth: '',
    },
    mode: 'onChange',
  });

  // Navigation blocker for unsaved changes
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  // Fetch FK dropdown data
  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries', 'dropdown'],
    queryFn: ministriesService.getAllForDropdown,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: masterService.getDepartments,
  });
  // Shared, cached list of ALL units (see useMinimalUnits) — reused from cache
  // across every unit picker instead of re-hitting the API per screen.
  const { data: units = [] } = useMinimalUnits();
  const { data: designations = [] } = useQuery({
    queryKey: ['designations'],
    queryFn: masterService.getDesignations,
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');

  // The selected project's RBAC policy decides whether a unit is used at all.
  // A unit-less project (unitModel === 'none') hides the Unit field entirely.
  const { data: projectSettings } = useQuery({
    queryKey: ['project', 'settings', selectedAppId],
    queryFn: () => projectsService.getSettings(selectedAppId!),
    enabled: !!selectedAppId,
    staleTime: 5 * 60 * 1000,
  });
  // Default to "used" until settings load, so a unit-using project never briefly
  // lets a personnel be created without a unit.
  const unitUsed = (projectSettings?.rbac?.unitModel ?? 'required') !== 'none';
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // A personnel's ministry/department/unit/designation must be same-project-or-global.
  const { belongsToProject, projectTag } = useProjectScope(selectedAppId, accessibleProjects);

  // Clear dependent FK selections that no longer belong to the chosen project.
  const prevAppIdRef = useRef<string | undefined>(undefined);
  const watchedMinistryId = watch('ministryId');
  const watchedDepartmentId = watch('departmentId');
  const watchedUnitId = watch('unitId');
  const watchedDesignationId = watch('designationId');
  useEffect(() => {
    const prev = prevAppIdRef.current;
    prevAppIdRef.current = selectedAppId;
    if (prev === undefined || prev === selectedAppId || !selectedAppId) return;
    const bad = (list: any[], selId?: string) => {
      if (!selId) return false;
      const it = list.find((x) => x._id === selId || x.id === selId);
      return it?.appId && it.appId !== selectedAppId;
    };
    if (bad(ministries, watchedMinistryId)) setValue('ministryId', '');
    if (bad(departments, watchedDepartmentId)) setValue('departmentId', '');
    if (bad(units, watchedUnitId)) setValue('unitId', '');
    if (bad(designations, watchedDesignationId)) setValue('designationId', '');
  }, [selectedAppId, ministries, departments, units, designations,
      watchedMinistryId, watchedDepartmentId, watchedUnitId, watchedDesignationId, setValue]);

  // Fetch personnel data for edit mode - wait for ID decryption
  const { data: personnel, isLoading: fetchLoading } = useQuery({
    queryKey: ['personnel', id],
    queryFn: () => personnelService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show personnel name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? personnel?.name : null);

  // Populate form when personnel data is loaded
  useEffect(() => {
    if (personnel && isEditMode) {
      const attributes = (personnel as any).attributes || {};
      const normalizedGender = (personnel.gender || '').toLowerCase();
      reset({
        email: personnel.email || '',
        name: personnel.name || '',
        firstName: personnel.firstName || '',
        lastName: personnel.lastName || '',
        phoneNo: personnel.phoneNo || '',
        gender: (normalizedGender === 'male' || normalizedGender === 'female' ? normalizedGender : '') as any,
        title: personnel.title || '',
        isOnDuty: (personnel as any).isOnDuty ?? false,
        appId: (personnel as any).appId || '',
        ministryId: personnel.ministryId || '',
        departmentId: personnel.departmentId || '',
        unitId: personnel.unitId || '',
        designationId: personnel.designationId || '',
        badgeNo: attributes.badgeNo || personnel.badgeNo || '',
        dateOfBirth: (attributes.dateOfBirth || personnel.dateOfBirth || '').split?.('T')[0] || '',
      });
    }
  }, [personnel, isEditMode, reset]);

  // Mutation for create/update
  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditMode && id) {
        return personnelService.update(id, data);
      }
      return personnelService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personnel'] });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Personnel ${isEditMode ? 'updated' : 'created'} successfully`,
        life: 3000,
      });
      setTimeout(() => navigateToList(), 1000);
    },
    onError: (error: any) => {
      const errorMessage = extractErrorMessage(
        error,
        `Failed to ${isEditMode ? 'update' : 'create'} personnel`
      );
      toast.current?.show({ severity: 'error', summary: 'Error', detail: errorMessage, life: 10000 });
    },
  });

  const isSaving = mutation.isPending;

  const onSubmit = (data: PersonnelFormData) => {
    const editing = isEditMode && !!personnel;
    const orig: any = editing ? personnel : {};

    // Unit is mandatory only for a project that uses units (unitModel !== 'none').
    // Mirrors the BE rule; keeps unit-less projects free of the requirement.
    if (unitUsed && !data.unitId) {
      setError('unitId', { type: 'manual', message: 'Unit is required' });
      toast.current?.show({ severity: 'error', summary: 'Unit required', detail: 'Select a unit for this project.', life: 6000 });
      return;
    }

    // appId is immutable on update, and the BE only re-validates an org FK when it is
    // PRESENT in the payload. So on update we send an org FK only when it actually
    // changed — this lets unrelated edits (name, phone, on-duty) save even if a legacy
    // FK is off-project, while still enforcing scope the moment an FK is changed.
    const fkChanged = (key: keyof PersonnelFormData, origVal: any) =>
      !editing || (data[key] || '') !== (origVal || '');
    const send = {
      ministryId: fkChanged('ministryId', orig.ministryId),
      departmentId: fkChanged('departmentId', orig.departmentId),
      unitId: fkChanged('unitId', orig.unitId),
      designationId: fkChanged('designationId', orig.designationId),
    };

    // Guard (mirrors the BE rule exactly): an org FK that has its own project must match
    // the personnel's project by _id; a GLOBAL personnel (no appId) may reference ONLY
    // global FKs. Runs even when the personnel has no project, so we surface a clear
    // message instead of the raw 422 — names can collide across projects, ids can't.
    {
      const personnelApp = data.appId || null; // '' / undefined ⇒ global personnel
      const fkAppId = (list: any[], selId?: string): string | null => {
        if (!selId) return null;
        const it = list.find((x) => x._id === selId || x.id === selId);
        return (it?.appId ?? null) as string | null;
      };
      const scoped = ([
        send.ministryId && ['ministryId', 'Ministry', fkAppId(ministries, data.ministryId)],
        send.departmentId && ['departmentId', 'Department', fkAppId(departments, data.departmentId)],
        send.unitId && ['unitId', 'Unit', fkAppId(units, data.unitId)],
        send.designationId && ['designationId', 'Designation', fkAppId(designations, data.designationId)],
      ].filter(Boolean)) as Array<[keyof PersonnelFormData, string, string | null]>;
      // A scoped FK (fkApp !== null) is invalid when the personnel is global OR when its
      // project differs. A global FK (fkApp === null) is always allowed.
      const mismatched = scoped.filter(([, , fkApp]) => fkApp !== null && fkApp !== personnelApp);
      if (mismatched.length) {
        mismatched.forEach(([field, label]) =>
          setError(field as any, { type: 'manual', message: `${label} belongs to a different project.` })
        );
        const detail = personnelApp
          ? `${mismatched.map(([, l]) => l).join(', ')} ${mismatched.length === 1 ? 'belongs' : 'belong'} to a different project. Pick ${mismatched.length === 1 ? 'one' : 'ones'} in this personnel's project or global.`
          : `This personnel has no project (global), so ${mismatched.map(([, l]) => l).join(', ')} must be global too. Re-scope the personnel to a project first, or pick global ${mismatched.length === 1 ? 'value' : 'values'}.`;
        toast.current?.show({ severity: 'error', summary: 'Project mismatch', detail, life: 9000 });
        return;
      }
    }

    // Build attributes object from exposed common keys
    const attributes: Record<string, unknown> = {};
    if (data.badgeNo) attributes.badgeNo = data.badgeNo;
    if (data.dateOfBirth) attributes.dateOfBirth = data.dateOfBirth;

    // Flat BE contract — send ONLY the allowed fields.
    const payload: any = {
      email: data.email,
      name: data.name,
      firstName: data.firstName || undefined,
      lastName: data.lastName || undefined,
      phoneNo: data.phoneNo || undefined,
      gender: data.gender || undefined,
      title: data.title || undefined,
      isOnDuty: data.isOnDuty ?? false,
      // Owning project — honoured on create only (immutable on update, so omit it there).
      appId: editing ? undefined : data.appId || undefined,
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
    };
    // Org FKs: always on create (required); on update only when changed (see above).
    if (send.ministryId) payload.ministryId = data.ministryId;
    if (send.departmentId) payload.departmentId = data.departmentId;
    if (send.unitId) payload.unitId = data.unitId;
    if (send.designationId) payload.designationId = data.designationId || undefined;

    mutation.mutate(payload);
  };

  // Dropdown options
  const ministryOptions = useMemo(
    () => ministries.filter((m) => belongsToProject((m as any).appId))
      .map((m) => ({ value: m._id, label: m.name, ...projectTag((m as any).appId) })),
    [ministries, belongsToProject, projectTag]
  );
  const departmentOptions = useMemo(
    () => departments.filter((d) => belongsToProject((d as any).appId))
      .map((d) => ({ value: d._id, label: d.name, ...projectTag((d as any).appId) })),
    [departments, belongsToProject, projectTag]
  );
  const unitOptions = useMemo(
    () => units.filter((u) => belongsToProject((u as any).appId))
      .map((u) => ({ value: u._id, label: u.unitCode ? `${u.name} (${u.unitCode})` : u.name, ...projectTag((u as any).appId) })),
    [units, belongsToProject, projectTag]
  );
  const designationOptions = useMemo(
    () => designations.filter((d) => belongsToProject((d as any).appId))
      .map((d) => ({ value: d._id, label: d.name, ...projectTag((d as any).appId) })),
    [designations, belongsToProject, projectTag]
  );

  // Local filter search handlers for searchable selects
  const makeSearch = (opts: { value: string; label: string }[]) => async (term: string) =>
    opts.filter((o) => o.label.toLowerCase().includes(term.toLowerCase()));

  if (fetchLoading) {
    return <PageSkeleton panels={3} />;
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.PERSONNEL}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 bg-canvas dark:bg-gray-900">
        {/* Header Section */}
        <ScreenHeader icon="pi pi-users" title={isEditMode ? 'Edit Personnel' : 'Create New Personnel'} subtitle={isEditMode ? 'Update personnel information' : 'Add a new personnel to the system'} onBack={() => handleNavigate('/personnel')} />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Personal Information Section */}
          <div className="bg-surface rounded-lg shadow p-6 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Controller
                name="name"
                control={control}
                render={({ field: { value, ...fieldProps } }) => (
                  <FormInput
                    {...fieldProps}
                    value={value || ''}
                    label="Full Name"
                    placeholder="Enter full name"
                    error={!!errors.name}
                    helperText={errors.name?.message as string}
                    required
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Email"
                    type="email"
                    placeholder="user@example.com"
                    required
                    error={!!errors.email}
                    helperText={errors.email?.message as string}
                  />
                )}
              />

              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="First Name"
                    placeholder="Enter first name"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message as string}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Last Name"
                    placeholder="Enter last name"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message as string}
                  />
                )}
              />

              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Title"
                    placeholder="e.g. Mr, Ms, Dr"
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                  />
                )}
              />

              <Controller
                name="gender"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Gender"
                    options={genderOptions}
                    showClear
                    error={!!errors.gender}
                    helperText={errors.gender?.message as string}
                  />
                )}
              />

              <Controller
                name="phoneNo"
                control={control}
                render={({ field }) => (
                  <PhoneField
                    name="phoneNo"
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label="Phone Number"
                    error={!!errors.phoneNo}
                    helperText={errors.phoneNo?.message as string}
                  />
                )}
              />

              <div className="flex items-center gap-3">
                <Controller
                  name="isOnDuty"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <InputSwitch checked={!!value} onChange={(e: any) => onChange(e.value)} />
                      <label className="text-sm text-gray-700 dark:text-gray-300">On Duty</label>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Organization Information Section */}
          <div className="bg-surface rounded-lg shadow p-6 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Organization
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Controller
                  name="appId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Project"
                      options={projectOptions}
                      placeholder="Select project"
                      // Owning project is immutable on update — change it via re-scope instead.
                      disabled={projectLocked || isEditMode}
                      error={!!errors.appId}
                      helperText={errors.appId?.message as string}
                    />
                  )}
                />
              </div>
              <Controller
                name="ministryId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSearchableSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Ministry"
                    required
                    onSearch={makeSearch(ministryOptions)}
                    initialOptions={ministryOptions}
                    placeholder="Search and select ministry"
                    error={!!errors.ministryId}
                    helperText={errors.ministryId?.message as string}
                  />
                )}
              />

              <Controller
                name="departmentId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSearchableSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Department"
                    required
                    onSearch={makeSearch(departmentOptions)}
                    initialOptions={departmentOptions}
                    placeholder="Search and select department"
                    error={!!errors.departmentId}
                    helperText={errors.departmentId?.message as string}
                  />
                )}
              />

              {/* Unit is shown only for projects that use units (unitModel !== 'none'). */}
              {unitUsed && (
                <Controller
                  name="unitId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSearchableSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Unit"
                      required
                      onSearch={makeSearch(unitOptions)}
                      initialOptions={unitOptions}
                      placeholder="Search and select unit"
                      error={!!errors.unitId}
                      helperText={errors.unitId?.message as string}
                    />
                  )}
                />
              )}

              <Controller
                name="designationId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSearchableSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Designation"
                    onSearch={makeSearch(designationOptions)}
                    initialOptions={designationOptions}
                    placeholder="Search and select designation"
                    error={!!errors.designationId}
                    helperText={errors.designationId?.message as string}
                  />
                )}
              />
            </div>
          </div>

          {/* Additional Attributes Section */}
          <div className="bg-surface rounded-lg shadow p-6 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Additional Attributes (Optional)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Controller
                name="badgeNo"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Badge Number"
                    placeholder="Enter badge number"
                    error={!!errors.badgeNo}
                    helperText={errors.badgeNo?.message as string}
                  />
                )}
              />

              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <FormDatePicker
                    {...field}
                    value={field.value || ''}
                    label="Date of Birth"
                    error={!!errors.dateOfBirth}
                    helperText={errors.dateOfBirth?.message as string}
                    maxDate={new Date().toISOString().split('T')[0]}
                  />
                )}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/personnel')}
              disabled={isSaving}
            />
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      {/* Leave Confirmation Dialog */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="PersonnelForm.Dialog.Leave"
      />
    </PermissionGuard>
  );
};

export default PersonnelForm;
